import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Code2, Eye, Mail, Plus, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { EmailPreviewFrame } from "@/components/admin/EmailPreviewFrame";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyTemplate = {
  id: "",
  key: "",
  name: "",
  description: "",
  subject: "",
  heading: "",
  body_html: "",
  is_system: false,
  business_usable: false,
};

const templateIdentity = (template) => template?.id || template?.key || "";
const templatePriority = (template) => {
  if (template?.key === "thank_you_for_joining_614_shop_local") return 0;
  if (template?.is_system) return 1;
  return 2;
};
const sortTemplates = (emailTemplates) => [...emailTemplates].sort((a, b) => {
  const priorityDifference = templatePriority(a) - templatePriority(b);
  if (priorityDifference !== 0) return priorityDifference;
  return (a.name || "").localeCompare(b.name || "");
});

export const SystemEmailManagement = ({ token }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(emptyTemplate);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editorMode, setEditorMode] = useState("rich");

  const selectedTemplate = useMemo(
    () => templates.find((template) => templateIdentity(template) === selectedId),
    [templates, selectedId],
  );

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/system-emails`, { headers });
      const nextTemplates = sortTemplates(response.data || []);
      setTemplates(nextTemplates);
      setSelectedId((current) => {
        const selected = nextTemplates.find((template) => templateIdentity(template) === current) || nextTemplates[0];
        if (selected) {
          setForm({ ...emptyTemplate, ...selected });
          return templateIdentity(selected);
        }
        setForm(emptyTemplate);
        return "";
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load system emails");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (selectedTemplate) setForm({ ...emptyTemplate, ...selectedTemplate });
  }, [selectedTemplate]);

  const handleNew = () => {
    setSelectedId("");
    setForm(emptyTemplate);
    setEditorMode("rich");
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.subject.trim()) {
      toast.error("Name and subject are required");
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const identity = templateIdentity(form);
      if (identity) {
        const response = await axios.put(`${API}/admin/system-emails/${identity}`, {
          name: form.name,
          description: form.description,
          subject: form.subject,
          heading: form.heading,
          body_html: form.body_html,
          business_usable: !!form.business_usable,
        }, { headers });
        setTemplates((previous) => previous.map((template) => templateIdentity(template) === identity ? response.data : template));
        setForm({ ...emptyTemplate, ...response.data });
      } else {
        const response = await axios.post(`${API}/admin/system-emails`, {
          key: form.key,
          name: form.name,
          description: form.description,
          subject: form.subject,
          heading: form.heading,
          body_html: form.body_html,
          business_usable: !!form.business_usable,
        }, { headers });
        setTemplates((previous) => sortTemplates([...previous, response.data]));
        setSelectedId(templateIdentity(response.data));
        setForm({ ...emptyTemplate, ...response.data });
      }
      toast.success("System email saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save system email");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const identity = templateIdentity(form);
    if (!identity || form.is_system) return;
    if (!window.confirm("Delete this system email?")) return;

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/system-emails/${identity}`, { headers });
      const nextTemplates = templates.filter((template) => templateIdentity(template) !== identity);
      setTemplates(nextTemplates);
      setSelectedId(templateIdentity(nextTemplates[0]));
      setForm(nextTemplates[0] ? { ...emptyTemplate, ...nextTemplates[0] } : emptyTemplate);
      toast.success("System email deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete system email");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="rounded-2xl border border-gray-100 bg-white p-6 text-sm text-gray-500">Loading system emails...</div>;
  }

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[18rem_minmax(0,1fr)]">
      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-lg font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>System Emails</h2>
          <Button type="button" size="sm" onClick={handleNew} className="rounded-full bg-[#1a1a1a] px-4 text-white" data-testid="add-system-email-btn">
            <Plus className="mr-2 h-4 w-4" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {templates.map((template) => (
            <button
              key={templateIdentity(template)}
              type="button"
              onClick={() => setSelectedId(templateIdentity(template))}
              className={`w-full rounded-xl border p-3 text-left transition-colors ${
                selectedId === templateIdentity(template) ? "border-[#42D78A] bg-[#42D78A]/10" : "border-gray-100 hover:bg-gray-50"
              }`}
            >
              <div className="flex items-start gap-2">
                <Mail className="mt-0.5 h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-sm font-bold text-[#1a1a1a]">{template.name}</p>
                  <p className="mt-1 break-all text-xs text-gray-500">{template.key}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(22rem,0.8fr)]">
      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {templateIdentity(form) ? "Edit Email" : "Add System Email"}
            </h2>
            <p className="mt-1 text-sm text-gray-500">Use rich text or trusted HTML/CSS for formatting, images, GIFs, and links.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {form.is_system && <Badge className="w-fit bg-blue-100 text-blue-800">Default</Badge>}
            {form.business_usable && <Badge className="w-fit bg-[#42D78A]/15 text-[#157347]">Business usable</Badge>}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="mt-2" data-testid="system-email-name" />
          </div>
          <div>
            <Label>Key</Label>
            <Input value={form.key} onChange={(e) => setForm((p) => ({ ...p, key: e.target.value }))} disabled={!!templateIdentity(form)} className="mt-2" data-testid="system-email-key" />
          </div>
          <div>
            <Label>Subject</Label>
            <Input value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} className="mt-2" data-testid="system-email-subject" />
          </div>
          <div>
            <Label>Heading</Label>
            <Input value={form.heading} onChange={(e) => setForm((p) => ({ ...p, heading: e.target.value }))} className="mt-2" data-testid="system-email-heading" />
          </div>
          <div className="md:col-span-2">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} className="mt-2 min-h-[70px]" data-testid="system-email-description" />
          </div>
          <div className="md:col-span-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Label>Email Body</Label>
              <div className="flex rounded-full bg-gray-100 p-1">
                {[
                  { value: "rich", label: "Rich Text", icon: Mail },
                  { value: "html", label: "HTML", icon: Code2 },
                ].map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setEditorMode(mode.value)}
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                      editorMode === mode.value ? "bg-[#1a1a1a] text-white" : "text-gray-600 hover:text-[#1a1a1a]"
                    }`}
                    data-testid={`system-email-mode-${mode.value}`}
                  >
                    <mode.icon className="h-3.5 w-3.5" /> {mode.label}
                  </button>
                ))}
              </div>
            </div>
            {editorMode === "html" ? (
              <Textarea
                value={form.body_html}
                onChange={(event) => setForm((p) => ({ ...p, body_html: event.target.value }))}
                className="mt-2 min-h-[360px] font-mono text-xs"
                spellCheck={false}
                data-testid="system-email-body-html"
              />
            ) : (
              <div className="mt-2">
                <RichTextEditor
                  key={templateIdentity(form) || "new-template"}
                  value={form.body_html}
                  onChange={(value) => setForm((p) => ({ ...p, body_html: value }))}
                  testId="system-email-body"
                  imageUploadEndpoint="/api/upload/message-image"
                  videoUploadEndpoint="/api/upload/message-video"
                  token={token}
                />
              </div>
            )}
            <p className="mt-2 text-xs text-gray-500">
              Available variables include: {"{{recipient_name}}"}, {"{{profile_type}}"}, {"{{account_email}}"}, {"{{login_url}}"}, {"{{site_url}}"}, {"{{business_name}}"}, {"{{business_email}}"}, {"{{market_name}}"}, {"{{market_email}}"}.
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={handleSave} disabled={saving} className="rounded-full bg-[#42D78A] px-6 font-bold text-[#1a1a1a] hover:bg-[#3bc27c]" data-testid="save-system-email-btn">
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Saving..." : "Save Email"}
          </Button>
          {templateIdentity(form) && !form.is_system && (
            <Button onClick={handleDelete} disabled={saving} variant="outline" className="rounded-full text-red-600 hover:bg-red-50">
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>
      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <Eye className="h-5 w-5 text-[#42D78A]" />
          <div>
            <h3 className="text-lg font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Preview</h3>
            <p className="text-xs text-gray-500">Rendered with sample variables and the 614 Shop Local email wrapper.</p>
          </div>
        </div>
        <EmailPreviewFrame
          subject={form.subject}
          heading={form.heading}
          bodyHtml={form.body_html}
          testId="system-email-preview"
        />
      </div>
      </div>
    </div>
  );
};
